import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, ConfirmDialog, EmptyState, ErrorState, StatusPill, type StatusTone } from '@bb/ui';
import {
  ApiError,
  type CreateEventExportConnectorBody,
  type EventExportConnectorStatus,
  type EventExportConnectorView,
  type EventExportReconcileStatus,
  type EventExportS3Config,
  type EventExportTestResult,
} from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { formatWhen } from '../segments/SegmentsListPage.tsx';

const ROUTE = '/env/$environmentId/settings/event-export';

const STATUS: Record<EventExportConnectorStatus, [string, StatusTone]> = {
  ok: ['OK', 'success'],
  lagging: ['Lagging', 'warning'],
  failing: ['Failing', 'danger'],
  disabled: ['Disabled', 'neutral'],
  archived: ['Archived', 'neutral'],
};
const RECONCILE: Record<EventExportReconcileStatus, [string, StatusTone]> = {
  ok: ['OK', 'success'],
  missing: ['Missing', 'danger'],
  late_facts: ['Late facts', 'warning'],
  shrunk: ['Shrunk', 'info'],
  pending: ['Pending', 'neutral'],
};
const CONDITIONAL_WRITE: Record<NonNullable<EventExportConnectorView['conditionalWrite']>, string> = {
  if_none_match: 'Atomic (If-None-Match)',
  head_then_put: 'Check then write (not atomic)',
  unsupported: 'Not supported',
};
const PURPOSE = { analytics: 'Analytics', archive: 'Compliance archive' } as const;
const IDENTIFIERS = { none: 'No identifiers', profile_id: 'Profile and device IDs', external_id: 'Profile, device and external IDs' } as const;

/** The server's message with its problem code, so a failed request can be traced. */
const problemText = (error: unknown) => (error instanceof ApiError ? `${error.message} (${error.code})` : 'Something went wrong.');

/** "Up to date", "25 min behind", "2 h behind", "3 d behind". */
export function humanizeLag(seconds: number): string {
  if (seconds < 60) return 'Up to date';
  if (seconds < 3_600) return `${Math.floor(seconds / 60)} min behind`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)} h behind`;
  return `${Math.floor(seconds / 86_400)} d behind`;
}

/** A `datetime-local` value read as UTC (`YYYY-MM-DDTHH:mm` → ISO with milliseconds). */
const utcIso = (local: string) => `${local}:00.000Z`;
const utcHour = (iso: string) => `${iso.slice(0, 10)} ${iso.slice(11, 13)}:00`;
const count = (n: number) => n.toLocaleString('en-US');
const byType = (counts: Record<string, number>) =>
  Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([type, n]) => `${type} ${count(n)}`)
    .join(' · ') || '—';

function destination(c: EventExportConnectorView): string {
  if (c.kind !== 's3') return c.kind;
  const config = c.config as Partial<EventExportS3Config>;
  return `s3://${config.bucket ?? '?'}/${config.prefix ?? ''}${config.endpoint ? ` at ${config.endpoint}` : ''}`;
}

function CreateConnectorForm({ environmentId, onDone }: { environmentId: string; onDone: () => void }) {
  const { api } = useRouteContext({ from: ROUTE });
  const queryClient = useQueryClient();
  const canExportExternalIds = usePermissions().has('pii.view');
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState<'analytics' | 'archive'>('analytics');
  const [region, setRegion] = useState('');
  const [bucket, setBucket] = useState('');
  const [prefix, setPrefix] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [pathStyle, setPathStyle] = useState(false);
  const [sse, setSse] = useState<'' | 'AES256' | 'aws:kms'>('');
  const [kmsKeyId, setKmsKeyId] = useState('');
  const [lock, setLock] = useState<'' | 'GOVERNANCE' | 'COMPLIANCE'>('');
  const [retainDays, setRetainDays] = useState('365');
  const [secretRef, setSecretRef] = useState('');
  const [factTypes, setFactTypes] = useState('');
  const [identifierMode, setIdentifierMode] = useState<keyof typeof IDENTIFIERS>('profile_id');
  const [acknowledged, setAcknowledged] = useState(false);
  const [startFrom, setStartFrom] = useState('');
  const create = useMutation({
    mutationFn: (body: CreateEventExportConnectorBody) => api.createEventExportConnector(environmentId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['event-export', environmentId] });
      onDone();
    },
  });
  const types = factTypes
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const ready = Boolean(name.trim() && region.trim() && bucket.trim() && secretRef.trim()) && (identifierMode !== 'external_id' || acknowledged) && (!lock || Number(retainDays) >= 1);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    const config: EventExportS3Config = {
      region: region.trim(),
      bucket: bucket.trim(),
      prefix: prefix.trim(),
      forcePathStyle: pathStyle,
      ...(endpoint.trim() ? { endpoint: endpoint.trim() } : {}),
      ...(sse ? { sse: { mode: sse, ...(sse === 'aws:kms' && kmsKeyId.trim() ? { kmsKeyId: kmsKeyId.trim() } : {}) } } : {}),
      ...(lock ? { objectLock: { mode: lock, retainDays: Number(retainDays) } } : {}),
    };
    create.mutate({
      kind: 's3',
      purpose,
      name: name.trim(),
      config,
      secretRef: secretRef.trim(),
      datasets: ['message_fact'],
      factTypes: types.length ? types : null,
      identifierMode,
      ...(identifierMode === 'external_id' ? { acknowledgeErasureConflict: true } : {}),
      enabled: true,
      ...(startFrom ? { startFrom: utcIso(startFrom) } : {}),
    });
  };
  return (
    <form className="stack-form panel" aria-label="New connector" onSubmit={submit}>
      <h2>New connector</h2>
      <label>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        Purpose
        <select value={purpose} onChange={(e) => setPurpose(e.target.value as 'analytics' | 'archive')}>
          <option value="analytics">Analytics (warehouse)</option>
          <option value="archive">Compliance archive</option>
        </select>
      </label>
      <fieldset>
        <legend>S3 bucket</legend>
        <label>
          Region
          <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="eu-central-1" />
        </label>
        <label>
          Bucket
          <input value={bucket} onChange={(e) => setBucket(e.target.value)} />
        </label>
        <label>
          Prefix
          <input value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="bb" />
        </label>
        <label>
          Endpoint (optional)
          <input type="url" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="https://s3.example.internal" />
        </label>
        <p className="hint">Leave the endpoint empty for AWS; set it for MinIO, SeaweedFS or another S3-compatible store.</p>
        <label>
          <input type="checkbox" checked={pathStyle} onChange={(e) => setPathStyle(e.target.checked)} /> Path-style addressing
        </label>
        <label>
          Server-side encryption
          <select value={sse} onChange={(e) => setSse(e.target.value as typeof sse)}>
            <option value="">None</option>
            <option value="AES256">AES256</option>
            <option value="aws:kms">aws:kms</option>
          </select>
        </label>
        {sse === 'aws:kms' && (
          <label>
            KMS key ID
            <input value={kmsKeyId} onChange={(e) => setKmsKeyId(e.target.value)} />
          </label>
        )}
        <label>
          Object Lock
          <select value={lock} onChange={(e) => setLock(e.target.value as typeof lock)}>
            <option value="">None</option>
            <option value="GOVERNANCE">GOVERNANCE</option>
            <option value="COMPLIANCE">COMPLIANCE</option>
          </select>
        </label>
        {lock && (
          <label>
            Retain days
            <input type="number" min={1} max={36500} value={retainDays} onChange={(e) => setRetainDays(e.target.value)} />
          </label>
        )}
      </fieldset>
      <label>
        Secret reference
        <input value={secretRef} onChange={(e) => setSecretRef(e.target.value)} placeholder="env:BB_EXPORT_S3" />
      </label>
      <p className="hint">
        Where the service reads the bucket credentials: <code>env:NAME</code> (a variable of the service) or <code>file:/path</code> (inside its secrets directory). Credentials are set on the server, never here.
      </p>
      <label>
        Fact types
        <input value={factTypes} onChange={(e) => setFactTypes(e.target.value)} placeholder="sent, delivered, opened" />
      </label>
      <p className="hint">Comma-separated. Leave empty to export every type.</p>
      <fieldset>
        <legend>Identifiers in exported files</legend>
        {(Object.keys(IDENTIFIERS) as Array<keyof typeof IDENTIFIERS>).map((mode) => (
          <label key={mode}>
            <input type="radio" name="identifier-mode" checked={identifierMode === mode} disabled={mode === 'external_id' && !canExportExternalIds} onChange={() => setIdentifierMode(mode)} /> {IDENTIFIERS[mode]}
          </label>
        ))}
        {!canExportExternalIds && <p className="hint">Exporting external IDs needs pii.view.</p>}
        {identifierMode === 'external_id' && (
          <label>
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} /> I understand exported files keep external IDs after a privacy erase
          </label>
        )}
      </fieldset>
      <label>
        Start from (UTC, optional)
        <input type="datetime-local" value={startFrom} onChange={(e) => setStartFrom(e.target.value)} />
      </label>
      <p className="hint">Default: the start of the current hour. Earlier history goes through a replay.</p>
      {create.isError && <p role="alert">{problemText(create.error)}</p>}
      <div className="toolbar">
        <Button type="submit" variant="primary" disabled={!ready} loading={create.isPending}>
          Create connector
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

function ReplayForm({ environmentId, connector }: { environmentId: string; connector: EventExportConnectorView }) {
  const { api } = useRouteContext({ from: ROUTE });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const replay = useMutation({ mutationFn: () => api.replayEventExport(environmentId, connector.id, { from: utcIso(from), to: utcIso(to) }) });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (from && to) replay.mutate();
  };
  return (
    <form className="inline-form" aria-label={`Replay ${connector.name}`} onSubmit={submit}>
      <label>
        From (UTC) <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
      </label>
      <label>
        To (UTC) <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
      </label>
      <Button type="submit" disabled={!from || !to} loading={replay.isPending}>
        Queue replay
      </Button>
      <p className="hint">Whole UTC hours, at most 31 days. A window with the same content is skipped; a changed one is written as a new version.</p>
      {replay.isSuccess && <p role="status">{replay.data.windows === 1 ? '1 window queued' : `${count(replay.data.windows)} windows queued`}</p>}
      {replay.isError && <p role="alert">{problemText(replay.error)}</p>}
    </form>
  );
}

function WindowsPanel({ environmentId, connectorId }: { environmentId: string; connectorId: string }) {
  const { api } = useRouteContext({ from: ROUTE });
  const windows = useInfiniteQuery({
    queryKey: ['event-export', environmentId, connectorId, 'windows'],
    queryFn: ({ pageParam }) => api.eventExportWindows(environmentId, connectorId, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = windows.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <section aria-label="Written windows">
      <h3>Written windows</h3>
      {windows.isError ? (
        <p role="alert">{problemText(windows.error)}</p>
      ) : !windows.data ? (
        <p aria-busy="true">Loading windows…</p>
      ) : items.length === 0 ? (
        <p className="hint">No window written yet.</p>
      ) : (
        <table className="table" aria-label="Windows">
          <thead>
            <tr>
              <th scope="col">Window (UTC)</th>
              <th scope="col">Version</th>
              <th scope="col">Rows</th>
              <th scope="col">By type</th>
              <th scope="col">Parts</th>
            </tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={`${w.windowStart}-${w.version}`}>
                <td>{utcHour(w.windowStart)}</td>
                <td>v{w.version}</td>
                <td>{count(w.rowCount)}</td>
                <td>{byType(w.rowsByType)}</td>
                <td>{w.parts.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {windows.hasNextPage && (
        <Button onClick={() => void windows.fetchNextPage()} loading={windows.isFetchingNextPage}>
          Load more
        </Button>
      )}
    </section>
  );
}

function ReconcilePanel({ environmentId, connectorId }: { environmentId: string; connectorId: string }) {
  const { api } = useRouteContext({ from: ROUTE });
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [checked, setChecked] = useState<string | null>(null);
  const report = useQuery({
    queryKey: ['event-export', environmentId, connectorId, 'reconcile', checked],
    queryFn: () => api.eventExportReconcile(environmentId, connectorId, checked!),
    enabled: Boolean(checked),
  });
  return (
    <section aria-label="Reconcile">
      <h3>Reconcile</h3>
      <p className="hint">Compares the facts in the database with the highest exported version of every hour of a UTC day.</p>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (date) setChecked(date);
        }}
      >
        <label>
          Day (UTC) <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <Button type="submit" disabled={!date}>
          Check
        </Button>
      </form>
      {report.isError && <p role="alert">{problemText(report.error)}</p>}
      {checked && report.isPending && <p aria-busy="true">Comparing…</p>}
      {report.data && (
        <>
          <p>{report.data.ok ? <StatusPill tone="success">Every closed hour matches</StatusPill> : <StatusPill tone="warning">Some hours need attention</StatusPill>}</p>
          <table className="table" aria-label={`Reconcile ${report.data.date}`}>
            <thead>
              <tr>
                <th scope="col">Hour (UTC)</th>
                <th scope="col">Status</th>
                <th scope="col">In database</th>
                <th scope="col">Exported</th>
                <th scope="col">Version</th>
              </tr>
            </thead>
            <tbody>
              {report.data.windows.map((w) => {
                const [label, tone] = RECONCILE[w.status];
                return (
                  <tr key={w.windowStart}>
                    <td>{w.windowStart.slice(11, 16)}</td>
                    <td>
                      <StatusPill tone={tone}>{label}</StatusPill>
                    </td>
                    <td title={byType(w.db.byType)}>{count(w.db.rows)}</td>
                    <td title={w.exported ? byType(w.exported.byType) : undefined}>{w.exported ? count(w.exported.rows) : '—'}</td>
                    <td>{w.exported ? `v${w.exported.version}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={2}>
                  Total
                </th>
                <td>{count(report.data.totals.db.rows)}</td>
                <td>{count(report.data.totals.exported.rows)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
          <p className="hint">Late facts are written again as a new version; shrunk means rows were erased (privacy or retention) after the export and is informational.</p>
        </>
      )}
    </section>
  );
}

function ConnectorCard({ environmentId, connector: c }: { environmentId: string; connector: EventExportConnectorView }) {
  const { api } = useRouteContext({ from: ROUTE });
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState<'replay' | 'details' | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [testResult, setTestResult] = useState<EventExportTestResult | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['event-export', environmentId] });
  const toggle = useMutation({ mutationFn: () => api.updateEventExportConnector(environmentId, c.id, { enabled: !c.enabled }), onSuccess: refresh });
  const test = useMutation({
    mutationFn: () => api.testEventExportConnector(environmentId, c.id),
    onMutate: () => setTestResult(null),
    onSuccess: async (result) => {
      setTestResult(result);
      await refresh();
    },
  });
  const archive = useMutation({
    mutationFn: () => api.archiveEventExportConnector(environmentId, c.id),
    onSuccess: async () => {
      setConfirmArchive(false);
      await refresh();
    },
  });
  const [label, tone] = STATUS[c.status];
  const active = c.status !== 'archived';
  return (
    <article aria-label={`Connector ${c.name}`} className="panel">
      <h2>
        {c.name} <StatusPill tone={tone}>{label}</StatusPill>
      </h2>
      <dl className="facts">
        <dt>Destination</dt>
        <dd>
          {c.kind.toUpperCase()} · {PURPOSE[c.purpose]} · <code>{destination(c)}</code>
        </dd>
        <dt>Exported until</dt>
        <dd>
          {formatWhen(c.exportedUntil)} · {humanizeLag(c.lagSeconds)}
        </dd>
        <dt>Identifiers</dt>
        <dd>{IDENTIFIERS[c.identifierMode]}</dd>
        <dt>Fact types</dt>
        <dd>{c.factTypes ? c.factTypes.join(', ') : 'All'}</dd>
        <dt>Secret reference</dt>
        <dd>
          <code>{c.secretRef}</code>
        </dd>
        <dt>Conditional write</dt>
        <dd>{c.conditionalWrite ? CONDITIONAL_WRITE[c.conditionalWrite] : 'Not tested yet'}</dd>
        <dt>Last error</dt>
        <dd>{c.lastError ? `${c.lastError}${c.consecutiveFailures ? ` (${c.consecutiveFailures} failed runs in a row)` : ''}` : '—'}</dd>
      </dl>
      {c.locked && <p className="hint">Purpose and identifiers are locked after the first written window; create a new connector to change them.</p>}
      <div className="toolbar">
        {active && (
          <>
            <Button onClick={() => toggle.mutate()} loading={toggle.isPending}>
              {c.enabled ? 'Disable' : 'Enable'}
            </Button>
            <Button onClick={() => test.mutate()} loading={test.isPending}>
              Test connection
            </Button>
            <Button aria-expanded={panel === 'replay'} onClick={() => setPanel(panel === 'replay' ? null : 'replay')}>
              Replay
            </Button>
          </>
        )}
        <Button aria-expanded={panel === 'details'} onClick={() => setPanel(panel === 'details' ? null : 'details')}>
          Windows and reconcile
        </Button>
        {active && (
          <Button variant="danger" onClick={() => setConfirmArchive(true)}>
            Archive
          </Button>
        )}
      </div>
      {testResult && (
        <p role="status">
          {testResult.ok ? (
            <StatusPill tone="success">Connection works</StatusPill>
          ) : (
            <StatusPill tone="danger">Connection failed</StatusPill>
          )}{' '}
          {testResult.conditionalWrite && `Conditional write: ${CONDITIONAL_WRITE[testResult.conditionalWrite]}. `}
          {testResult.message}
          {testResult.code && <> ({testResult.code})</>}
        </p>
      )}
      {(toggle.isError || test.isError || archive.isError) && <p role="alert">{problemText(toggle.error ?? test.error ?? archive.error)}</p>}
      {panel === 'replay' && <ReplayForm environmentId={environmentId} connector={c} />}
      {panel === 'details' && (
        <>
          <WindowsPanel environmentId={environmentId} connectorId={c.id} />
          <ReconcilePanel environmentId={environmentId} connectorId={c.id} />
        </>
      )}
      <ConfirmDialog open={confirmArchive} onOpenChange={setConfirmArchive} title={`Archive ${c.name}?`} confirmLabel="Archive connector" onConfirm={() => archive.mutate()} busy={archive.isPending}>
        <p>The export stops. Files already written stay in the bucket.</p>
      </ConfirmDialog>
    </article>
  );
}

/**
 * Settings → Event export (BB-137): connectors that write message facts as hourly gzip JSON Lines files to an S3
 * bucket (analytics or a compliance archive), their lag and errors, connection test, replay, written windows and
 * reconcile. Everything needs integrations.manage; credentials stay on the server behind a secret reference.
 */
export function EventExportPage() {
  const { environmentId } = useParams({ from: ROUTE });
  const { api } = useRouteContext({ from: ROUTE });
  const [creating, setCreating] = useState(false);
  const list = useQuery({ queryKey: ['event-export', environmentId], queryFn: () => api.listEventExportConnectors(environmentId) });
  return (
    <section>
      <h1>Event export</h1>
      <p className="hint">Message facts leave as immutable gzip JSON Lines files, one closed UTC hour at a time, each hour committed by its manifest.</p>
      <p className="notice notice--warning" role="note">
        A privacy erase does not rewrite files that were already exported: they keep the erased user&apos;s facts and, when external IDs are exported, the external ID.
      </p>
      {!creating && (
        <Button variant="primary" onClick={() => setCreating(true)}>
          New connector
        </Button>
      )}
      {creating && <CreateConnectorForm environmentId={environmentId} onDone={() => setCreating(false)} />}
      {list.isError ? (
        <ErrorState title="Connectors could not be loaded" message={problemText(list.error)} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <p aria-busy="true">Loading connectors…</p>
      ) : list.data.items.length === 0 ? (
        <EmptyState title="No connectors yet" description="Add a connector to export message facts to your bucket." />
      ) : (
        <ul aria-label="Connectors" className="plain-list">
          {list.data.items.map((c) => (
            <li key={c.id}>
              <ConnectorCard environmentId={environmentId} connector={c} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
